"use client";

import { useState, useEffect } from "react";
import { Save, Loader2, Trash2, Image as ImageIcon } from "lucide-react";
import Image from "next/image";
import MediaLibrary from "@/components/MediaLibrary";
import RichEditor from "@/components/RichEditor";
import { getDirectDriveLink } from "@/lib/text";

const convertGoogleDriveUrl = (url: string) => getDirectDriveLink(url);

export default function AdminProfilePage() {
    const [form, setForm] = useState({
        bio: "", phone: "", email: "", address: "",
        linkedin: "", github: "", youtube: "", facebook: "",
        whatsapp: "", freelance: "", tiktok: "", avatar: ""
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");
    const [showMediaLibrary, setShowMediaLibrary] = useState(false);

    useEffect(() => {
        fetch("/api/profile").then(r => r.json()).then(data => {
            if (data) {
                const cleanData = { ...form };
                Object.keys(data).forEach(key => {
                    if (key in cleanData) (cleanData as any)[key] = data[key] || "";
                });
                setForm(cleanData);
            }
        }).catch(() => { });
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true); setSuccess(false); setError("");

        // Final conversion check before saving
        const finalForm = { ...form, avatar: convertGoogleDriveUrl(form.avatar) };

        try {
            const res = await fetch("/api/profile", {
                method: "PUT",
                body: JSON.stringify(finalForm),
                headers: { "Content-Type": "application/json" }
            });
            if (res.ok) {
                setForm(finalForm);
                setSuccess(true);
                setTimeout(() => setSuccess(false), 3000);
            }
            else { setError("Erreur lors de la sauvegarde"); }
        } catch { setError("Erreur réseau"); }
        finally { setLoading(false); }
    };

    const inputStyle = { width: "100%", padding: "0.875rem 1rem", border: "1.5px solid #e2e8f0", borderRadius: "0.75rem", background: "#f8fafc", fontSize: "0.95rem", outline: "none", boxSizing: "border-box" as const, color: "#0f172a" };
    const labelStyle = { display: "block", marginBottom: "0.5rem", fontSize: "0.78rem", fontWeight: 800, textTransform: "uppercase" as const, letterSpacing: "0.08em", color: "#64748b" };

    const Field = ({ label, k, type = "text", placeholder = "" }: { label: string; k: keyof typeof form; type?: string; placeholder?: string }) => (
        <div>
            <label style={labelStyle}>{label}</label>
            {k === "bio"
                ? <RichEditor value={form.bio} onChange={(bio) => setForm({ ...form, bio })} placeholder={placeholder} />
                : (type === "textarea"
                    ? <textarea rows={4} value={(form as any)[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} style={{ ...inputStyle, resize: "vertical" }} placeholder={placeholder} />
                    : <input type={type} value={(form as any)[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} style={inputStyle} placeholder={placeholder} />
                )
            }
        </div>
    );

    return (
        <div>
            {showMediaLibrary && (
                <MediaLibrary
                    onSelect={(url) => { setForm({ ...form, avatar: url }); setShowMediaLibrary(false); }}
                    onClose={() => setShowMediaLibrary(false)}
                />
            )}

            <div style={{ marginBottom: "2.5rem" }}>
                <h1 style={{ fontFamily: "Outfit", fontSize: "2rem", fontWeight: 900, color: "#0f172a" }}>Mon Profil & Réseaux</h1>
                <p style={{ color: "#64748b" }}>Gérez vos informations personnelles et liens sociaux.</p>
            </div>

            <form onSubmit={handleSubmit}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "2rem" }} className="profile-grid">
                    <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
                        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "1.5rem", padding: "2.5rem", display: "flex", flexDirection: "column", gap: "2rem" }}>
                            <h2 style={{ fontFamily: "Outfit", fontWeight: 900, fontSize: "1.25rem", color: "#0f172a" }}>Photo de Profil (Avatar)</h2>
                            <div style={{ display: "flex", flexDirection: "column", gap: "2rem", alignItems: "start" }} className="avatar-section">
                                <div onClick={() => setShowMediaLibrary(true)} style={{ width: 160, height: 160, borderRadius: "2rem", background: "#f8fafc", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", border: "3px dashed #e2e8f0", position: "relative", cursor: "pointer", flexShrink: 0 }}>
                                    {form.avatar ? (
                                        <Image src={getDirectDriveLink(form.avatar)} alt="Avatar" fill style={{ objectFit: "cover" }} />
                                    ) : (
                                        <div style={{ textAlign: "center", color: "#94a3b8" }}>
                                            <ImageIcon size={40} style={{ margin: "0 auto 0.75rem" }} />
                                            <p style={{ fontSize: "0.7rem", fontWeight: 800 }}>CLIQUER POUR MODIFIER</p>
                                        </div>
                                    )}
                                </div>
                                <div style={{ flex: 1, width: "100%", display: "flex", flexDirection: "column", gap: "1rem" }}>
                                    <div>
                                        <label style={labelStyle}>URL DIRECTE DE L'IMAGE</label>
                                        <input type="text" value={form.avatar} onChange={e => setForm({ ...form, avatar: e.target.value })}
                                            placeholder="https://exemple.com/ma-photo.jpg" style={inputStyle} />
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                                        <div style={{ height: "1px", flex: 1, background: "#f1f5f9" }} />
                                        <span style={{ fontSize: "0.7rem", fontWeight: 800, color: "#cbd5e1" }}>OU</span>
                                        <div style={{ height: "1px", flex: 1, background: "#f1f5f9" }} />
                                    </div>
                                    <button type="button" onClick={() => setShowMediaLibrary(true)} style={{ width: "100%", padding: "1rem", background: "#f1f5f9", border: "2px solid #e2e8f0", borderRadius: "1rem", fontWeight: 800, fontSize: "0.9rem", cursor: "pointer", transition: "all 0.2s" }}>
                                        Ouvrir la Bibliothèque / Importer
                                    </button>
                                    <p style={{ fontSize: "0.75rem", color: "#64748b", fontStyle: "italic" }}>
                                        Tip: Vous pouvez coller un lien Google Drive ou une URL directe ici.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "1.25rem", padding: "2rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}>
                            <h2 style={{ fontFamily: "Outfit", fontWeight: 900, fontSize: "1.1rem", color: "#0f172a" }}>À Propos de moi</h2>
                            <Field label="Bio Professionnelle" k="bio" type="textarea" placeholder="Technologue béninois spécialisé en IoT..." />
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem" }}>
                                <Field label="Email public" k="email" type="email" placeholder="contact@example.com" />
                                <Field label="Téléphone" k="phone" placeholder="+229 XX XX XX XX" />
                            </div>
                            <Field label="Localisation" k="address" placeholder="Bénin, Afrique de l'Ouest" />
                        </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
                        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "1.25rem", padding: "2rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}>
                            <h2 style={{ fontFamily: "Outfit", fontWeight: 900, fontSize: "1.1rem", color: "#0f172a" }}>Réseaux & Liens</h2>
                            <Field label="WhatsApp (ex: +229XXXXXXXX)" k="whatsapp" placeholder="+229XXXXXX" />
                            <Field label="LinkedIn (URL)" k="linkedin" placeholder="https://linkedin.com/in/..." />
                            <Field label="GitHub (URL)" k="github" placeholder="https://github.com/..." />
                            <Field label="Page Freelance (URL)" k="freelance" placeholder="https://comeup.com/@..." />
                            <Field label="YouTube (URL)" k="youtube" placeholder="https://youtube.com/@..." />
                            <Field label="Facebook (URL)" k="facebook" placeholder="https://facebook.com/..." />
                            <Field label="TikTok (URL)" k="tiktok" placeholder="https://tiktok.com/@..." />
                        </div>

                        {success && <div style={{ padding: "0.875rem", background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: "0.75rem", color: "#16a34a", fontWeight: 700, fontSize: "0.875rem" }}>✅ Sauvegardé avec succès !</div>}
                        {error && <div style={{ padding: "0.875rem", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: "0.75rem", color: "#dc2626", fontWeight: 600, fontSize: "0.875rem" }}>{error}</div>}

                        <button type="submit" disabled={loading} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.6rem", padding: "1.1rem", background: "#2563eb", color: "#fff", border: "none", borderRadius: "0.875rem", fontWeight: 900, fontSize: "1rem", cursor: "pointer", boxShadow: "0 4px 16px rgba(37,99,235,0.3)" }}>
                            {loading ? <><Loader2 size={20} className="animate-spin" /> Sauvegarde...</> : <><Save size={20} /> Enregistrer le Profil</>}
                        </button>
                    </div>
                </div>
            </form>
            <style>{`@media(min-width:900px){.profile-grid{grid-template-columns:3fr 2fr !important;}}`}</style>
        </div>
    );
}
